/* eslint-disable @typescript-eslint/no-explicit-any */
import { Filters } from 'components/TablesReportFilter';
import { WaitlistFilters } from 'state/serviceReports';
import { core } from '@tock/coretypes';
import { dashboard } from 'types';
import AppConstants from 'constants/AppConstants';
import protos from 'utils/protos';

const {
  TablesReportRequest,
  TransactionReportRequest,
  RedemptionReportRequest,
  WaitlistReportRequest,
  AdminDownloadRequest,
} = protos;

export interface BasicReportRequest {
  startDate?: string;
  endDate?: string;
}

type BackFillReportRequestInput =
  | dashboard.TablesReportRequest
  | dashboard.TransactionReportRequest
  | dashboard.IRedemptionReportRequest
  | dashboard.IWaitlistReportRequest
  | BasicReportRequest;

function backFillEndDateInRequest(request: BackFillReportRequestInput) {
  return {
    ...request,
    endDate: request.endDate ?? request.startDate,
  };
}

export function getTablesReport(request: any): ApiAction {
  const tablesReportRequest = backFillEndDateInRequest(request);
  return {
    TockApi: {
      postListProto: '/reports/service',
      request: TablesReportRequest.fromObject(tablesReportRequest),
      type: AppConstants.REPORT_TABLES_FETCH,
      discardOldRequests: true,
    },
  };
}

export function setTablesReportHasExperienceSelected(hasExperienceSelected: any): BaseTockAction {
  return {
    type: AppConstants.REPORT_TABLES_REPORT_HAS_EXPERIENCE_SELECTED,
    payload: hasExperienceSelected,
  };
}

export function getTransactionsReport(request: dashboard.TransactionReportRequest): ApiAction {
  const transactionReportRequest = backFillEndDateInRequest(request);
  return {
    TockApi: {
      postListProto: '/reports/transactions',
      request: TransactionReportRequest.fromObject(transactionReportRequest),
      type: AppConstants.REPORT_TRANSACTIONS_FETCH,
    },
  };
}

export function getRedemptionsReport(request: dashboard.IRedemptionReportRequest): ApiAction {
  const redemptionReportRequest = backFillEndDateInRequest(request);
  return {
    TockApi: {
      postListProto: '/reports/redemptions',
      request: RedemptionReportRequest.fromObject(redemptionReportRequest),
      type: AppConstants.REPORT_REDEMPTIONS_FETCH,
    },
  };
}

export function getGuestsReport(request: BasicReportRequest): ApiAction {
  const guestReportRequest = backFillEndDateInRequest(request);
  return {
    TockApi: {
      getListProto: `/reports/guests?startDate=${guestReportRequest.startDate}&endDate=${guestReportRequest.endDate}`,
      type: AppConstants.REPORT_GUESTS_FETCH,
    },
  };
}

export function getTableManagementReport(request: BasicReportRequest): ApiAction {
  const tableManagementReportRequest = backFillEndDateInRequest(request);
  return {
    TockApi: {
      getListProto: `/reports/tablemanagement?startDate=${tableManagementReportRequest.startDate}&endDate=${tableManagementReportRequest.endDate}`,
      type: AppConstants.REPORT_TABLE_MANAGEMENT_FETCH,
    },
  };
}

export function updateTableManagementReportPurchase(purchase: core.IPastPurchase): BaseTockAction {
  return {
    type: AppConstants.REPORT_TABLE_MANAGEMENT_FETCH_UPDATE_PURCHASE,
    payload: purchase,
  };
}

export function getWaitlistsReport(request: dashboard.IWaitlistReportRequest): ApiAction {
  const waitlistReportRequest = backFillEndDateInRequest(request);
  return {
    TockApi: {
      postListProto: `/reports/waitlist/v2`,
      request: WaitlistReportRequest.fromObject(waitlistReportRequest),
      type: AppConstants.REPORT_WAITLISTS_FETCH,
    },
  };
}

export function getSubscriptionReport(): ApiAction {
  return {
    TockApi: {
      getListProto: '/reports/subscription',
      type: AppConstants.REPORT_SUBSCRIPTION_FETCH,
    },
  };
}

/**
 * Downloads a report for a given date range.
 * @param {string} reportName - the server defined name of the report to download.
 * @param {object} startDate - the start date as a LocalDate object.
 * @param {object} endDate - the end date as a LocalDate object.
 * @returns {Object} A redux TockApi action.
 */
export function getDownloadToken(
  reportName: any,
  startDate: string | null,
  endDate: string | null
): ApiAction {
  const downloadType = reportName.toUpperCase() + '_CSV';
  const request = AdminDownloadRequest.fromObject({
    downloadType,
    startDate: startDate ? startDate.toString() : '',
    endDate: endDate ? endDate.toString() : '',
  });
  return {
    TockApi: {
      postProto: '/download',
      request,
      type: AppConstants.DOWNLOAD,
    },
  };
}

export function setBookingsReportFilters(filters: Filters) {
  return {
    type: AppConstants.SET_BOOKINGS_REPORT_FILTERS,
    payload: filters,
  };
}

export function setWaitlistReportFilter(filters: WaitlistFilters) {
  return {
    type: AppConstants.SET_WAITLIST_REPORT_FILTER,
    payload: filters,
  };
}

export function setWaitlistReportTextFilter(filterText: string) {
  return {
    type: AppConstants.SET_WAITLIST_REPORT_TEXT_FILTER,
    payload: filterText,
  };
}

export function setData(payload: any) {
  return (dispatch: Dispatch) =>
    new Promise<void>((resolve) => {
      dispatch({
        type: AppConstants.REPORT_SET_DATA,
        payload,
      });
      resolve();
    });
}
